/**
 * `addCollateral` — the only Tier-1 effect that travels over HTTP rather than
 * the socket.
 *
 * A custodian's `addCollateral` transaction moves real DAI on-chain. Today the
 * backend is told about it from a `useEffect` in the component, so closing the
 * tab in that window leaves collateral posted on-chain that the dashboard never
 * reflects.
 *
 * REPLAY SAFETY — no `verify()` needed, and none is written.
 * The server treats this endpoint as a *projection* of chain state, not an
 * accumulator. `syncCollateralFromChain` advances `lastUpdatedCollateralBlock`
 * monotonically, and re-projecting the same or an older block is documented in
 * `transcation.service.ts` as "a safe no-op — NOT a client error". The legacy
 * float-math path, which would double-count, hard-rejects equal-or-older blocks
 * for exactly that reason. Both paths are therefore idempotent on their own
 * terms, so a retry needs no client-side pre-check.
 */

import axios from "axios";
import { registerEffect } from "../outbox";
import api from "../../config/api";

export const ADD_COLLATERAL_EFFECT = "addCollateral";

/**
 * The screen still calls this directly on the happy path, so the outbox is a
 * backstop. Long enough for the direct call to complete and for the on-chain
 * indexer to have a chance at the same event.
 */
const GRACE_PERIOD_MS = 20000;

async function send({ payload, entry, ctx }) {
  if (!ctx?.token) throw new Error("no auth token");

  // The hash is not known when the intent is declared — it is filled in from
  // the confirmed receipt at send time. The server rejects a payload whose
  // transaction is not a real FrdmRouter call, so this must be the true hash.
  const txHash = entry?.receipt?.transactionHash;
  if (!txHash) throw new Error("no confirmed transaction hash for addCollateral");

  const response = await axios.post(
    api.userDetails.addCollateral,
    { ...payload, txHash },
    { headers: { Authorization: `Bearer ${ctx.token}` } }
  );

  return response?.data?.success === false ? Promise.reject(
    new Error(response?.data?.message || "addCollateral rejected")
  ) : "applied";
}

registerEffect({
  type: ADD_COLLATERAL_EFFECT,
  send,
  gracePeriodMs: GRACE_PERIOD_MS,
});

export const __testing = { send, GRACE_PERIOD_MS };
